/**
 * How the prediction made before a contrast twin came out (pedagogy spec §5.3.6), at the top of the
 * twin's terminal feedback: "Din förutsägelse stämde." or "… stämde inte.", what was chosen (and, if
 * it was wrong, what was right), and the authored reason. It arrives only with a terminal
 * response, so a withheld first try shows no verdict; the retry's does. Renders nothing without a
 * verdict (an older answer, "Jag kör fast", a link that authors no prediction).
 */
import { CheckCircle2, XCircle } from "lucide-react";
import { Localized } from "@client/components/Localized";
import { useT } from "@client/i18n";
import { PredictionChoice } from "./PredictionCard";
import type { QuestionResult } from "./session";

export interface PredictionVerdictProps {
  result: Pick<QuestionResult, "predictionResult" | "followUp">;
}

export function PredictionVerdict({ result }: PredictionVerdictProps) {
  const t = useT();
  const verdict = result.predictionResult;
  if (!verdict) return null;
  const options = result.followUp?.prediction?.options;
  const Icon = verdict.correct ? CheckCircle2 : XCircle;

  return (
    <div
      role="status"
      className={
        verdict.correct
          ? "mb-4 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-ink-800 dark:border-green-800 dark:bg-green-950/50 dark:text-ink-100"
          : "mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-ink-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-ink-100"
      }
    >
      <p className="flex items-center gap-2 font-medium">
        <Icon
          size={16}
          aria-hidden="true"
          className={
            verdict.correct
              ? "shrink-0 text-green-600 dark:text-green-400"
              : "shrink-0 text-amber-600 dark:text-amber-400"
          }
        />
        {verdict.correct ? t("practice.prediction.right") : t("practice.prediction.wrong")}
      </p>
      <p className="mt-1">
        {t("practice.prediction.chosen")}:{" "}
        <PredictionChoice choice={verdict.choice} options={options} />
        {!verdict.correct && (
          <>
            {" · "}
            {t("practice.prediction.expected")}:{" "}
            <PredictionChoice choice={verdict.expected} options={options} />
          </>
        )}
      </p>
      <div className="mt-2 text-ink-700 dark:text-ink-200">
        <Localized value={verdict.why} variant="compact" />
      </div>
    </div>
  );
}
